import { useState } from 'react';
import { useT } from '@kmgestao/i18n';
import { Badge, PageContainer, PageIntroCard } from '@kmgestao/ui';

import { SuperadminShell } from '../../SuperadminShell.js';
import type { ProviderRow } from '../../../../features/ai-admin/index.js';

import { DeleteProviderDialog } from './DeleteProviderDialog.js';
import { ModuleAssignments } from './ModuleAssignments.js';
import { ProviderForm } from './ProviderForm.js';
import { ProvidersList } from './ProvidersList.js';

/**
 * Página Superadmin de configuração de IA.
 * - Lista Providers cadastrados (com status da chave e "Testar").
 * - Modal de CRUD (criar/editar).
 * - Atribuição por módulo (8 linhas, uma Select por módulo de IA).
 *
 * Protegida por `<RequireAuth>` + `<RequireSuperadmin>` no App.tsx.
 */
export function SuperadminAiSettingsPage() {
  const { t } = useT('ai-admin');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ProviderRow | null>(null);
  const [toDelete, setToDelete] = useState<ProviderRow | null>(null);

  const handleAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const handleEdit = (provider: ProviderRow) => {
    setEditing(provider);
    setFormOpen(true);
  };

  const handleDelete = (provider: ProviderRow) => setToDelete(provider);

  return (
    <SuperadminShell>
      <PageContainer>
        <PageIntroCard
          eyebrow={t('page.breadcrumb')}
          title={t('page.title')}
          description={t('page.subtitle')}
          meta={
            <>
              <Badge>{t('providers.title')}</Badge>
              <Badge variant="outline">{t('modules.title')}</Badge>
            </>
          }
        />

        <ProvidersList onAdd={handleAdd} onEdit={handleEdit} onDelete={handleDelete} />

        <ModuleAssignments />
      </PageContainer>

      <ProviderForm open={formOpen} onOpenChange={setFormOpen} editing={editing} />
      <DeleteProviderDialog provider={toDelete} onClose={() => setToDelete(null)} />
    </SuperadminShell>
  );
}
